import { IMAGE_KINDS } from "@ondo/shared";
import type { ArtistSummary, ImageKind } from "@ondo/shared";
import { useId, useRef, useState } from "react";

import { IMAGE_KIND_LABELS } from "../lib/constants.ts";
import { describeBytes } from "../lib/files.ts";
import {
  ACCEPTED_IMAGE_TYPES,
  ADDITIONAL_VIEWS,
  REGISTER_COPY,
} from "./register.constants.ts";
import type { DeclarationKey } from "./register.constants.ts";
import type { ArtistMatchView } from "./useRegisterController.ts";
import type {
  ArtworkValues,
  DeclarationRow,
  PendingImage,
  SummaryRow,
  TargetOption,
  ViewRow,
} from "./register.model.ts";

/**
 * Step 1.
 *
 * The phone row opens onto an explanation rather than a QR code. Every storage
 * policy on the registrations bucket is `to authenticated`, so a code that let a
 * phone upload before signing in would be a credential with no principal behind
 * it and no policy able to scope it. The row keeps its place in the flow because
 * the capture app is the intended path — it just says so honestly until the
 * phone can exchange that link for the same user's session.
 */
export function UploadImagesPanel({
  images,
  views,
  refusals,
  signed,
  onFiles,
  onRemove,
  onSetKind,
  onToggleSigned,
  onToggleView,
}: {
  images: PendingImage[];
  views: ViewRow[];
  refusals: string[];
  signed: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  onSetKind: (id: string, kind: ImageKind) => void;
  onToggleSigned: (signed: boolean) => void;
  onToggleView: (kind: ImageKind) => void;
}) {
  const listed = new Set(views.map((view) => view.kind));

  return (
    <div className="register__panel">
      <h2 className="register__panel-title">Upload Images</h2>

      <details className="register__phone">
        <summary>
          <span className="register__phone-icon" aria-hidden="true">
            📱
          </span>
          <span className="register__phone-copy">
            <strong>{REGISTER_COPY.phoneTitle}</strong>
            <span>{REGISTER_COPY.phoneBody}</span>
          </span>
          <span className="register__phone-badge">{REGISTER_COPY.phoneBadge}</span>
        </summary>
        <p className="register__help">{REGISTER_COPY.captureUnavailable}</p>
      </details>

      <p className="register__or">
        <span>{REGISTER_COPY.uploadDivider}</span>
      </p>

      <Dropzone onFiles={onFiles} />

      {refusals.length > 0 && (
        <ul className="register__refusals" role="alert">
          {refusals.map((refusal) => (
            <li key={refusal}>{refusal}</li>
          ))}
        </ul>
      )}

      {images.length > 0 && (
        <ul className="register__thumbs">
          {images.map((image) => (
            <li key={image.id} className="register__thumb">
              <img src={image.previewUrl} alt={image.file.name} />
              <select
                aria-label={`What ${image.file.name} shows`}
                value={image.kind}
                onChange={(event) => onSetKind(image.id, event.target.value as ImageKind)}
              >
                {IMAGE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {IMAGE_KIND_LABELS[kind]}
                  </option>
                ))}
              </select>
              <span className="register__thumb-meta">{describeBytes(image.file.size)}</span>
              <button type="button" onClick={() => onRemove(image.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="register__help">{REGISTER_COPY.imagesNotStored}</p>

      <label className="register__check">
        <input
          type="checkbox"
          checked={signed}
          onChange={(event) => onToggleSigned(event.target.checked)}
        />
        {REGISTER_COPY.signedLabel}
      </label>

      <section className="register__views">
        <h3>{REGISTER_COPY.viewsTitle}</h3>
        <p className="register__help">{REGISTER_COPY.viewsHelp}</p>

        <ul className="register__view-list">
          {views.map((view) => (
            <li
              key={view.kind}
              className={view.satisfied ? "register__view register__view--done" : "register__view"}
            >
              <span className="register__view-mark" aria-hidden="true">
                {view.satisfied ? "✓" : "○"}
              </span>
              {view.label}
              {view.removable && (
                <button
                  type="button"
                  aria-label={`Remove ${view.label} from the required views`}
                  onClick={() => onToggleView(view.kind)}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>

        <div className="register__view-add">
          {ADDITIONAL_VIEWS.filter((kind) => !listed.has(kind)).map((kind) => (
            <button key={kind} type="button" onClick={() => onToggleView(kind)}>
              + {IMAGE_KIND_LABELS[kind]}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * Drag, drop or browse.
 *
 * The dragging flag is local because it is not a fact about the registration:
 * it says where a pointer is, lives for the length of a gesture, and nothing
 * outside this element can act on it.
 */
function Dropzone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const picker = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      className={dragging ? "register__drop register__drop--over" : "register__drop"}
      onDragOver={(event) => {
        // Without this the browser navigates to the dropped file instead.
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        onFiles([...event.dataTransfer.files]);
      }}
    >
      <span className="register__drop-mark" aria-hidden="true">
        ⊕
      </span>
      <strong>{REGISTER_COPY.dropTitle}</strong>
      <span className="register__help">{REGISTER_COPY.dropFormats}</span>

      <input
        ref={picker}
        type="file"
        multiple
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        onChange={(event) => {
          onFiles([...(event.target.files ?? [])]);
          // Cleared so re-picking the same file after removing it still fires.
          event.target.value = "";
        }}
      />
      <button type="button" onClick={() => picker.current?.click()}>
        {REGISTER_COPY.browse}
      </button>
    </div>
  );
}

/** Step 2. No owner fields — the destination collection is the ownership record. */
export function ArtworkDetailsForm({
  values,
  targets,
  artists,
  routing,
  onSet,
  onSearch,
  onChooseArtist,
}: {
  values: ArtworkValues;
  targets: TargetOption[];
  artists: ArtistSummary[];
  routing: string;
  onSet: <K extends keyof ArtworkValues>(key: K, value: ArtworkValues[K]) => void;
  onSearch: (query: string) => void;
  onChooseArtist: (artist: ArtistSummary | null) => void;
}) {
  const targetField = useId();
  const titleField = useId();
  const artistField = useId();

  return (
    <div className="register__panel">
      <h2 className="register__panel-title">Artwork Details</h2>

      <div className="register__field">
        <label htmlFor={targetField}>Register into *</label>
        <select
          id={targetField}
          value={values.collectionId ?? ""}
          onChange={(event) => onSet("collectionId", event.target.value || null)}
        >
          <option value="">Choose a collection</option>
          {targets.map((target) => (
            <option key={target.id} value={target.id}>
              {target.label}
            </option>
          ))}
        </select>
        <p className="register__help">{REGISTER_COPY.destinationHelp}</p>
      </div>

      <div className="register__field">
        <label htmlFor={titleField}>Title *</label>
        <input
          id={titleField}
          value={values.title}
          placeholder="Fat Car"
          onChange={(event) => onSet("title", event.target.value)}
        />
      </div>

      <div className="register__field">
        <label htmlFor={artistField}>Artist *</label>
        <input
          id={artistField}
          value={values.attributedName}
          placeholder="Erwin Wurm"
          onChange={(event) => {
            onSet("attributedName", event.target.value);
            // Typing over a chosen artist unpicks it: the free text then
            // carries the claim, and the submission routes to ONDO.
            onChooseArtist(null);
            onSearch(event.target.value);
          }}
        />
        {artists.length > 0 && (
          <ul className="register__artists">
            {artists.map((artist) => (
              <li key={artist.id}>
                <button type="button" onClick={() => onChooseArtist(artist)}>
                  <span>{artist.displayName}</span>
                  <span className="register__artist-dates">
                    {artist.birthYear ?? "?"}–{artist.deathYear ?? ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* Says THAT an authority exists, never who. Naming it would make this
            form a directory of every estate ONDO has signed. */}
        <p className="register__help">{routing}</p>
      </div>

      <div className="register__row">
        <Field label="Year" value={values.year} onChange={(v) => onSet("year", v)} placeholder="2001" />
        <Field
          label="Medium"
          value={values.medium}
          onChange={(v) => onSet("medium", v)}
          placeholder="Painted polyester on car body"
        />
      </div>

      <div className="register__row">
        <Field
          label="Dimensions"
          value={values.dimensions}
          onChange={(v) => onSet("dimensions", v)}
          placeholder="137 × 477 × 220 cm"
        />
        <Field
          label="Edition"
          value={values.editionText}
          onChange={(v) => onSet("editionText", v)}
          placeholder="3/25"
        />
      </div>

      <div className="register__row">
        <Field
          label="Date conceived"
          value={values.dateConceived}
          onChange={(v) => onSet("dateConceived", v)}
          placeholder="2001"
        />
        <Field
          label="Date executed"
          value={values.dateExecuted}
          onChange={(v) => onSet("dateExecuted", v)}
          placeholder="2001"
        />
      </div>

      {/* No signature checkbox here: it is asked on the images step, where the
          answer changes which views the submission is expected to carry. */}
      <div className="register__field">
        <label htmlFor={`${titleField}-desc`}>Description</label>
        <textarea
          id={`${titleField}-desc`}
          rows={4}
          value={values.description}
          onChange={(event) => onSet("description", event.target.value)}
        />
      </div>
    </div>
  );
}

/**
 * OND-90. "Did you mean one of these?"
 *
 * Shown on the way out of step 2 when the artist's name was typed and no
 * artist was picked, and it is a bank transfer's confirmation screen rather
 * than a dropdown: one question, about a name the registrant has declared
 * finished, with picking the right artist as the easy path and declining as a
 * deliberate act that is told what it costs.
 *
 * WHAT IT MAY SHOW, which is the whole security boundary of this ticket. The
 * candidate rows are strings the model composed from a display name, a
 * nationality and dates. There is no authority, no estate, no studio and no
 * ONDO id in this panel, because `ondo.match_artists()` returns none — a row
 * naming the party appointed over an artist would make this step a walkable
 * directory of every estate ONDO has signed.
 */
export function ArtistMatchPanel({
  model,
  onChoose,
  onDecline,
}: {
  model: ArtistMatchView;
  onChoose: (artistId: string) => void;
  onDecline: () => void;
}) {
  return (
    <div className="register__panel">
      <h2 className="register__panel-title">{REGISTER_COPY.artistConfirmTitle}</h2>

      <p className="register__typed-name">
        You typed <strong>{model.typedName}</strong>
      </p>
      <p className="register__help">{REGISTER_COPY.artistConfirmHelp}</p>

      <ul className="register__candidates">
        {model.candidates.map((candidate) => (
          <li key={candidate.id}>
            <button
              type="button"
              className="register__candidate"
              onClick={() => onChoose(candidate.id)}
            >
              {candidate.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Says what choosing does and does not do, BEFORE the choice is made.
          OND-20's rule: a matched artist name is not approval. */}
      <p className="register__help" role="note">
        {REGISTER_COPY.artistConfirmNote}
      </p>

      {/* Never styled as the primary action, and never the default. Declining
          is a real answer the registrant is allowed to give — the ticket's "if
          they still say no" — and it is the one that loses the estate, so it is
          not the one the eye lands on first. */}
      <button type="button" className="register__decline" onClick={onDecline}>
        {REGISTER_COPY.artistConfirmNone}
      </button>
    </div>
  );
}

/** Step 4. */
export function ConfirmPanel({
  rows,
  declarations,
  onToggle,
}: {
  rows: SummaryRow[];
  declarations: DeclarationRow[];
  onToggle: (key: DeclarationKey) => void;
}) {
  return (
    <div className="register__panel">
      <h2 className="register__panel-title">Confirm Registration</h2>

      <dl className="register__summary">
        {rows.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>

      <div className="register__warning" role="note">
        <strong>Fraud warning</strong>
        <p>{REGISTER_COPY.fraudWarning}</p>
      </div>

      <fieldset className="register__declarations">
        <legend>Legal declarations</legend>
        {declarations.map((declaration) => (
          <label key={declaration.key} className="register__check">
            <input
              type="checkbox"
              checked={declaration.checked}
              onChange={() => onToggle(declaration.key)}
            />
            {declaration.label}
          </label>
        ))}
      </fieldset>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const id = useId();

  return (
    <div className="register__field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
